import React from 'react';
import styled from 'styled-components';
import {FlexRow, Icon, Text, tokensForThemeProvider} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import {useReportBuilder} from '../ReportBuilderContext';

const tokens = tokensForThemeProvider.sky.sys;
const compTokens = tokensForThemeProvider.sky.comp;

/** Informational-alert shell: a 12px solid accent bar flush on the left edge, the matching light-blue
 * informational background, and the design's asymmetric horizontal padding. `comp` is a sibling of
 * `sys` under `sky`, not nested inside it — `sys.color.background.accent` is the theme's bright yellow
 * and wrong for an "info" banner. */
const BannerWrapper = styled.div`
	display: flex;
	align-items: center;
	height: 60px;
	padding: ${tokens.spacing['200']} ${tokens.spacing['400']} ${tokens.spacing['200']} ${tokens.spacing['600']};
	background: ${compTokens.alerts.color.background.information};
	border-left: 12px solid ${compTokens.alerts.color.bar.information};
	border-radius: 4px;
	box-shadow: 0 2px 4px rgba(35, 35, 52, 0.12);
`;

/** The prompt is rendered italic and quoted. It is a sibling of the DS `Text` rather than a child:
 * `Text` processes children as text content and drops nested elements. */
const ItalicPromptText = styled.span`
	font-style: italic;
	color: ${compTokens.alerts.color.text.information};
`;

const SparkleIconWrapper = styled.span`
	display: inline-flex;
	align-items: center;
`;

/** Provenance for a report the agent generated, shown in edit mode only (a reader has no conditions to
 * reconcile against a prompt, and the design's read-mode frame hides this slot entirely).
 *
 * Two states, one source of truth. The conditions are what the report actually does; this line only
 * describes them. So the wording changes rather than the prompt being rewritten — the prompt is a
 * historical fact about what was asked, and stays quoted verbatim in both states. Once the report has
 * been hand-edited it simply stops claiming to BE that prompt.
 *
 * `manuallyEdited` combines what storage reported with anything edited this session, so the edited
 * wording survives a publish and a reload rather than resetting every time the builder reopens.
 *
 * There is deliberately no "Edit Prompt" action here: re-running a changed prompt would overwrite
 * conditions the editor may have tuned by hand, and the review removed it from the designs. */
export function AIBanner(): JSX.Element {
	const {formatMessage} = useIntl();
	const {prompt, manuallyEdited} = useReportBuilder();
	const infoColor = compTokens.alerts.color.text.information;

	// Edited wins over having a prompt to quote. A report can be hand-edited and carry no prompt text
	// (an older AI report, or one saved with an empty one), and that is exactly the case this line
	// exists to retract — claiming it was generated from a prompt would be the wrong half of the
	// sentence to keep.
	const labelId = manuallyEdited ? 'ai_report_builder.edited_since_generated' : 'ai_report_builder.generated_from_prompt';

	return (
		<BannerWrapper data-cy={'ai-report-banner'}>
			<FlexRow alignItems={'center'} gap={'s'}>
				<SparkleIconWrapper>
					<Icon icon={'sparkle'} size={'s'} color={compTokens.alerts.color.semanticicon.information} />
				</SparkleIconWrapper>
				<Text color={infoColor}>{formatMessage({id: labelId})}</Text>
				{prompt ? <ItalicPromptText>&ldquo;{prompt}&rdquo;</ItalicPromptText> : null}
			</FlexRow>
		</BannerWrapper>
	);
}
